import React, { useState, useEffect } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { setRequest } from "../../store/slices/memberSlice";
import { getPayrollRequests } from "../../services";
import { requestTypes, status } from "../../utils/Helpers";
import _ from "lodash";
import { motion, AnimatePresence } from "framer-motion";
import { ClipboardList, Info, Activity, Clock } from "lucide-react";
import Popup from "../../components/SharedComponets/Popup";
import Pagination from "../../components/Pagination";

const MemList = () => {
  const dispatch = useDispatch();
  const userProfile = useSelector((state) => state.user);
  const requests = useSelector((state) => state.members.requestMember);

  const [offset, setOffset] = useState(0);
  const itemsPerPage = 10;
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedContent, setSelectedContent] = useState("");

  const fetchRequest = async (offset) => {
    try {
      const response = await getPayrollRequests(
        userProfile.userinfo.Id, "", "", "", "", offset, itemsPerPage
      );
      dispatch(setRequest(response?.data?.Result));
    } catch (err) {
      console.error("Error fetching requests:", err);
    }
  };

  useEffect(() => {
    fetchRequest(offset);
  }, [offset]);

  const formatDate = (date, requestType) => {
    const d = moment(date);
    switch (requestType) {
      case "Leave":
      case "Breaks Adjustment":
        return d.format("DD MMM YYYY");
      case "CheckIn Adjustment":
      case "CheckOut Adjustment":
      case "Long Break":
      case "Overtime":
      case "Half Day":
        return d.format("DD MMM YYYY  h:mm A");
      default:
        return d.format("DD MMM YYYY");
    }
  };

  const formatCreatedAt = (date) => moment(date).format("DD MMM YYYY [AT] h:mm A");

  const handleOpenPopup = (content) => {
    setSelectedContent(content);
    setIsPopupOpen(true);
  };

  const statusConfig = {
    1: { label: "Pending", color: "amber" },
    2: { label: "Rejected", color: "red" },
    3: { label: "Approved", color: "green" },
  };

  const colorMap = {
    amber: {
      badge: "bg-amber-500/10 text-amber-500 border-amber-500/40",
      dot: "bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.5)]",
    },
    green: {
      badge: "bg-green-500/10 text-green-500 border-green-500/40",
      dot: "bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)]",
    },
    red: {
      badge: "bg-red-500/10 text-red-500 border-red-500/40",
      dot: "bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]",
    },
  };

  const hasRequests = !_.isEmpty(requests) && requests?.TotalCount > 0;

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
        className="space-y-3"
      >
        {/* Header row */}
        <div className="flex items-center justify-between mb-1 px-1 mt-6">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-500" />
            Previous Requests
          </h3>
          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
            Total: {requests?.TotalCount || 0}
          </span>
        </div>

        {/* Desktop List — Card per row */}
        <div className="hidden md:block bg-white dark:bg-gray-900/40 rounded-2xl border border-gray-100 dark:border-blue-gray-900 shadow-sm">
          {/* Column Headers — each column aligned to match row content */}
          <div className="grid grid-cols-[1.2fr_1.2fr_1.2fr_1.4fr_1.2fr_auto] gap-4 px-6 py-3 border-b border-gray-100 dark:border-gray-800/60 bg-gray-50/60 dark:bg-gray-800/40 items-center">
            <div className="text-xs font-semibold text-gray-400  uppercase tracking-widest">Request Type</div>
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Start Date</div>
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-widest">End Date</div>
            <div className="text-xs font-semibold text-gray-400  uppercase tracking-widest">Detail</div>
            <div className="text-xs font-semibold text-gray-400 -ml-2 uppercase tracking-widest">Created At</div>
            <div className="text-xs font-semibold text-gray-400 uppercase mr-10 tracking-widest text-right">Status</div>
          </div>

          {/* Rows */}
          <div className="p-3 space-y-2">
            {hasRequests ? (
              <AnimatePresence mode="popLayout">
                {requests?.RequestList?.map((request, index) => {
                  const cfg = statusConfig[request.RequestStatus];
                  const colors = cfg ? colorMap[cfg.color] : colorMap.amber;

                  return (
                    <motion.div
                      key={request.Id || index}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0, transition: { delay: index * 0.03 } }}
                      exit={{ opacity: 0, scale: 0.97 }}
                      className="grid grid-cols-[1.2fr_1.2fr_1.2fr_1.4fr_1.2fr_auto] gap-4 items-center px-6 py-4 bg-gray-50/50 dark:bg-gray-800/30 hover:bg-blue-50/30 dark:hover:bg-blue-900/10 rounded-xl border border-gray-100 dark:border-gray-800/40 transition-colors"
                    >
                      {/* Request Type */}
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="p-1.5 bg-blue-50 dark:bg-blue-500/10 rounded-lg flex-shrink-0">
                          <ClipboardList className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="text-[14px] font-light text-gray-700 dark:text-gray-300 truncate">
                          {requestTypes[request.RequestType]}
                        </div>
                      </div>

                      {/* Start Date */}
                      <div className="text-[14px] font-light text-gray-600 dark:text-gray-400 uppercase tracking-tight leading-relaxed">
                        {request.StartDate ? (
                          <>
                            <div>{moment(request.StartDate).format("DD MMM YY")}</div>
                            <div>{moment(request.StartDate).format("h:mm A")}</div>
                          </>
                        ) : (
                          "—"
                        )}
                      </div>

                      {/* End Date */}
                      <div className="text-[14px] font-light text-gray-600 dark:text-gray-400 uppercase tracking-tight leading-relaxed">
                        {request.EndDate && !_.isNull(request.EndDate) ? (
                          <>
                            <div>{moment(request.EndDate).format("DD MMM YY")}</div>
                            <div>{moment(request.EndDate).format("h:mm A")}</div>
                          </>
                        ) : (
                          "—"
                        )}
                      </div>

                      {/* Detail — always show View Details */}
                      <div className="min-w-0">
                        {request.RequestDetail ? (
                          <button
                            onClick={() => handleOpenPopup(request.RequestDetail)}
                            className="text-[14px] font-light text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                          >
                            View Details 
                          </button>
                        ) : (
                          <span className="text-[14px] font-light text-gray-400">—</span>
                        )}
                      </div>

                      {/* Created At */}
                      <div className="text-[14px] font-light text-gray-400 dark:text-gray-500 uppercase tracking-tight leading-relaxed">
                        <div>{moment(request.CreatedAt).format("DD MMM YY")}</div>
                        <div>{moment(request.CreatedAt).format("h:mm A")}</div>
                      </div>

                      {/* Status */}
                      <div className="flex items-center justify-end -mr-3">
                        <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-[13px] font-light border uppercase tracking-[0.08em] whitespace-nowrap ${colors.badge}`}>
                          <span className={`w-1.5 h-1.5 rounded-full me-1.5 ${colors.dot}`} />
                          {cfg?.label ?? "Pending"}
                        </span>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            ) : (
              <div className="py-20 flex flex-col items-center justify-center">
                <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-full mb-4">
                  <Info className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-500 dark:text-gray-400 font-medium text-base">
                  No requests found yet.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Cards */}
        <div className="flex flex-col gap-3 md:hidden">
          {hasRequests ? (
            <AnimatePresence mode="popLayout">
              {requests?.RequestList?.map((request, index) => {
                const cfg = statusConfig[request.RequestStatus];
                const colors = cfg ? colorMap[cfg.color] : colorMap.amber;

                return (
                  <motion.div
                    key={request.Id || index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white dark:bg-gray-900/40 border border-gray-100 dark:border-blue-gray-900 rounded-2xl p-5 shadow-sm space-y-4"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="p-2 bg-blue-50 dark:bg-blue-500/10 rounded-lg">
                          <ClipboardList className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        </div>
                        <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
                          {requestTypes[request.RequestType]}
                        </span>
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold border uppercase ${colors.badge}`}>
                        <span className={`w-1 h-1 rounded-full me-1.5 ${colors.dot}`} />
                        {cfg?.label ?? "Pending"}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-3 py-3 border-y border-gray-50 dark:border-gray-800/50">
                      <div>
                        <div className="text-[9px] text-gray-400 uppercase font-black tracking-widest mb-1">Start</div>
                        <div className="text-[11px] font-medium text-gray-700 dark:text-gray-300 uppercase">
                          {request.StartDate ? formatDate(request.StartDate, requestTypes[request.RequestType]) : "—"}
                        </div>
                      </div>
                      <div>
                        <div className="text-[9px] text-gray-400 uppercase font-black tracking-widest mb-1">End</div>
                        <div className="text-[11px] font-medium text-gray-700 dark:text-gray-300 uppercase">
                          {request.EndDate ? formatDate(request.EndDate, requestTypes[request.RequestType]) : "—"}
                        </div>
                      </div>
                    </div>

                    {request.RequestDetail && (
                      <div className="pb-3 border-b border-gray-50 dark:border-gray-800/50">
                        <button
                          onClick={() => handleOpenPopup(request.RequestDetail)}
                          className="text-[11px] font-bold text-blue-600 dark:text-blue-400 hover:text-blue-700 transition-colors"
                        >
                          View Details →
                        </button>
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <div className="text-[10px] text-gray-400 uppercase">
                        <Clock className="w-3 h-3 inline me-1" />
                        {formatCreatedAt(request.CreatedAt)}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          ) : (
            <div className="bg-white dark:bg-gray-900/40 rounded-2xl border border-gray-100 dark:border-blue-gray-900 p-10 text-center">
              <Info className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500 dark:text-gray-400 font-medium">No requests found yet.</p>
            </div>
          )}
        </div>

        {requests?.TotalCount > 0 && (
          <Pagination
            totalCount={requests.TotalCount}
            itemsPerPage={itemsPerPage}
            onPageChange={(newOffset) => setOffset(newOffset)}
          />
        )}
      </motion.div>

      {/* Detail Popup */}
      {isPopupOpen && (
        <Popup content={selectedContent} onClose={() => setIsPopupOpen(false)} />
      )}
    </>
  );
};

export default MemList;
